import { useMemo } from "react"
import { useLocale } from "./useLocale"

type NumberFactoryOptions = Readonly<{
    locale: string
}>

export class NumberFactory {
    public readonly locale: string
    public readonly decimalSeparator: string
    public readonly groupSeparator: string
    public readonly minusSign: string

    constructor({ locale }: NumberFactoryOptions) {
        this.locale = locale

        const numberFormat = Intl.NumberFormat(locale)

        this.decimalSeparator = numberFormat.formatToParts(1.1)
            .find(part => part.type === "decimal")
            ?.value ?? "."

        this.groupSeparator = numberFormat.formatToParts(1000000)
            .find(part => part.type === "group")
            ?.value ?? ","

        this.minusSign = numberFormat.formatToParts(-1)
            .find(part => part.type === "minusSign")
            ?.value ?? "-"
    }

    parse(value: string) {
        if (!value || value.trim() === "") return NaN

        const input = value.trim()

        // 1. Escape separators for use in Regex (dots need to be \.)
        const escapedDecimal = this.decimalSeparator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        //const escapedGroup = this.groupSeparator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        //const escapedMinus = this.minusSign.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

        // 2. Unicode Lenient Regex:
        // \p{Pd} matches any Dash/Hyphen ([:Dash:])
        // \p{Zs} matches any Separator/Space ([:Zs:])
        // We also include your locale's specific separators just in case they aren't in those categories
        const strictRule = new RegExp(
            `^[\\p{Pd}-]?\\d+(?:[\\p{Zs}${this.groupSeparator}]\\d+)*(?:${escapedDecimal}\\d+)?$`,
            "u" // The 'u' flag is mandatory for Unicode property escapes
        );

        if (!strictRule.test(input)) {
            return NaN;
        }

        // 3. Normalization for parseFloat
        // Replace ALL Unicode dashes with standard hyphen
        // Remove ALL Unicode spaces/group separators
        let normalized = input.replace(/\p{Pd}/gu, "-")
                             .replace(new RegExp(`[\\p{Zs}${this.groupSeparator}]`, "gu"), "")
                             .replace(this.decimalSeparator, ".");

        return parseFloat(normalized)
    }
}

export function useNumbers() {
    const locale = useLocale()
    const factory = useMemo(() => new NumberFactory({ locale }), [locale])

    return factory
}
